import { Component, DestroyRef, ElementRef, inject, OnInit, ViewChild } from '@angular/core';
import { EMAIL_VALIDATION_PATTERN } from '@shared/constant/shared.constant';
import { ToastService } from '@shared/services/toast.service';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { SuperAdminService } from '@core/services/super-admin.service';
import { autoUnsubWith } from '@shared/services/utility.abstract';

@Component({
  selector: 'app-shared-invoice-popup',
  templateUrl: './shared-invoice-popup.component.html',
  styleUrls: ['./shared-invoice-popup.component.scss'],
})
export class SharedInvoicePopupComponent implements OnInit{
  readonly destroyRef = inject(DestroyRef);
  @ViewChild('errorDiv') errorDiv!: ElementRef;
  emails: string[] = [];
  newEmail: string = '';
  isInvalidEmailFormat: boolean = false;
  isEmailAlreadyExists: boolean = false;
  error: string;
  isBookingExist: any;
  userEmail: any;
  bookingId: any;
  invoiceId: any;
  hideGuideLines: boolean = false;
  invalidEmailsList: string[];
  duplicateEmailsList: string[];
  userMobileNumber: any;

  constructor(
    private readonly ref: DynamicDialogRef,  
    private readonly toastService: ToastService,
    public readonly config: DynamicDialogConfig,
    private readonly superAdminService: SuperAdminService
  ) {}

  ngOnInit(): void {
    this.bookingId = this.config?.data?.bookingId;
    this.invoiceId = this.config?.data?.invoiceId;
    this.isBookingExist = this.config?.data?.isBookingExist;
    this.userEmail = this.config?.data?.email;
    this.userMobileNumber = this.config?.data?.mobileNumber;
  }

  addRecipient(): void {
    this.error = '';
    this.isInvalidEmailFormat = false;
    this.isEmailAlreadyExists = false;
  
    const emailInput = this.newEmail?.trim();
    if (!emailInput) return;
  
    let emailList = emailInput
      .split(',')
      .map(email => email.trim())
      .filter(email => email !== '');
  
    const invalidEmails: string[] = [];
    const duplicateEmails: Set<string> = new Set();
    const validEmails: Set<string> = new Set();
    const existingEmailsSet = new Set(this.emails); // Convert existing emails to a Set for faster lookup
  
    emailList.forEach(email => {
      if (!EMAIL_VALIDATION_PATTERN.test(email)) {
        invalidEmails.push(email);
      } else if (existingEmailsSet.has(email)) {
        duplicateEmails.add(email); // Add to duplicate list only once
      } else {
        validEmails.add(email); // Ensure unique valid emails
      }
    });
  
    if (invalidEmails.length > 0) {
      this.isInvalidEmailFormat = true;
      this.invalidEmailsList = invalidEmails;
      this.showErrorMsgFunc();
      this.newEmail = emailList.join(', ');
      return;
    }
  
    if (validEmails.has(this.userEmail)) {
      duplicateEmails.add(this.userEmail);
      validEmails.delete(this.userEmail);
    }
  
    if (duplicateEmails.size > 0) {
      this.isEmailAlreadyExists = true;
      this.duplicateEmailsList = Array.from(duplicateEmails);
      this.showErrorMsgFunc();
      this.newEmail = emailList.join(', ');
      return;
    }
  
    this.emails.push(...validEmails);
    this.error = '';
    this.newEmail = '';
    this.hideGuideLines = this.emails.length > 0;
  }
  
  removeRecipient(email: string) {
    this.emails = this.emails.filter((e) => e !== email);
    this.hideGuideLines = this.emails.length > 0;
  }

  showErrorMsgFunc() {
    if (this.isInvalidEmailFormat) {
      this.error = this.invalidEmailsList
        ? `Invalid email format for ${this.invalidEmailsList.map(email => `'${email}'`).join(", ")}.`
        : 'Invalid email format.';
    }
    if (this.isEmailAlreadyExists) {
      this.error = this.duplicateEmailsList
        ? `Email ${this.duplicateEmailsList.map(email => `'${email}'`).join(", ")} already added.`
        : 'Email already added.';
    }
  }

  close() {
    this.ref.close();
  }

  sendInvoice() {
    if (this.error) {
      this.scrollToError();
      return;
    }

const key = this.isBookingExist ? 'bookingId' : 'invoiceId';
    const emails = [...this.emails, this.userEmail];
  
    const request = {
      [key]: this.isBookingExist ? this.bookingId : this.invoiceId,
      emails: emails,
    };
  
    this.superAdminService.shareParkingInvoiceToDriver(request)
    .pipe(autoUnsubWith(this.destroyRef))
    .subscribe({
      next: ({ message }: any) => {
        this.toastService.showToastMessage('success', 'Success', message);
        this.closeDialog();
      },
      error: ({ error }: any) => {
        this.toastService.showToastMessage('error', 'Error', error?.message);
        this.closeDialog();
      },
    });
  }
  
  private scrollToError(): void {
    if (this.errorDiv) {
      this.errorDiv.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  private closeDialog(): void {
    
    this.ref.close();
  }

  clearError(): void {
    if (!this.newEmail?.trim()) {
      this.error = '';
      this.isInvalidEmailFormat = false;
      this.isEmailAlreadyExists = false;
    }
  }
}
